import { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import { Loader2 } from 'lucide-react';
import type { Student, CreateStudentInput } from '@frappi/shared';
import { createStudentSchema } from '@frappi/shared';

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';

const COLORS = [
  '#3B82F6', '#10B981', '#8B5CF6', '#F59E0B',
  '#EF4444', '#EC4899', '#06B6D4', '#84CC16',
  '#93C5FD', '#6EE7B7', '#C4B5FD', '#FDE68A',
  '#FCA5A5', '#F9A8D4', '#67E8F9', '#BEF264',
];

const EVAL_SCALE_OPTIONS = ['5', '10', '20', '100', 'A-F'] as const;

interface StudentFormProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  student?: Student | null;
  onSubmit: (data: CreateStudentInput) => void;
  isLoading?: boolean;
}

export function StudentForm({
  open,
  onOpenChange,
  student,
  onSubmit,
  isLoading,
}: StudentFormProps) {
  const { t } = useTranslation('students');
  const isEditing = !!student;
  const [isCustomScale, setIsCustomScale] = useState(false);

  const form = useForm<CreateStudentInput>({
    resolver: zodResolver(createStudentSchema),
    defaultValues: {
      name: '',
      grade: '',
      schoolName: '',
      identifyingColor: COLORS[0],
      notes: '',
      evalScale: '',
    },
  });

  useEffect(() => {
    if (student) {
      const scale = student.evalScale || '';
      const isPreset = EVAL_SCALE_OPTIONS.includes(scale as typeof EVAL_SCALE_OPTIONS[number]);
      setIsCustomScale(!!scale && !isPreset);
      form.reset({
        name: student.name,
        grade: student.grade,
        schoolName: student.schoolName,
        identifyingColor: student.identifyingColor,
        notes: student.notes || '',
        birthDate: student.birthDate || '',
        evalScale: scale,
      });
    } else {
      setIsCustomScale(false);
      form.reset({
        name: '',
        grade: '',
        schoolName: '',
        identifyingColor: COLORS[Math.floor(Math.random() * COLORS.length)],
        notes: '',
        evalScale: '',
      });
    }
  }, [student, form]);

  const handleSubmit = (data: CreateStudentInput) => {
    // Prevent double submission
    if (isLoading) return;
    // Clean empty evalScale
    const cleaned = { ...data, evalScale: data.evalScale || undefined };
    onSubmit(cleaned);
  };

  const selectedColor = form.watch('identifyingColor');

  const handleScaleSelect = (value: string) => {
    if (value === 'custom') {
      setIsCustomScale(true);
      form.setValue('evalScale', '');
    } else {
      setIsCustomScale(false);
      form.setValue('evalScale', value === 'none' ? '' : value);
    }
  };

  const currentScale = form.watch('evalScale') || '';
  const selectValue = isCustomScale
    ? 'custom'
    : EVAL_SCALE_OPTIONS.includes(currentScale as typeof EVAL_SCALE_OPTIONS[number])
      ? currentScale
      : currentScale ? 'custom' : 'none';

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[95vw] sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>
            {isEditing ? t('editStudent') : t('addStudent')}
          </DialogTitle>
          <DialogDescription>
            {isEditing ? t('editStudentDescription') : t('addStudentDescription')}
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('form.name')}</FormLabel>
                  <FormControl>
                    <Input placeholder={t('form.namePlaceholder')} {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
              <FormField
                control={form.control}
                name="grade"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('form.grade')}</FormLabel>
                    <FormControl>
                      <Input placeholder={t('form.gradePlaceholder')} {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="birthDate"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('form.birthDate')}</FormLabel>
                    <FormControl>
                      <Input type="date" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <FormField
              control={form.control}
              name="schoolName"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('form.schoolName')}</FormLabel>
                  <FormControl>
                    <Input placeholder={t('form.schoolNamePlaceholder')} {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Eval Scale */}
            <FormField
              control={form.control}
              name="evalScale"
              render={() => (
                <FormItem>
                  <FormLabel>{t('form.evalScale')}</FormLabel>
                  <div className="space-y-2">
                    <Select value={selectValue} onValueChange={handleScaleSelect}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder={t('form.evalScalePlaceholder')} />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="none">—</SelectItem>
                        {EVAL_SCALE_OPTIONS.map((opt) => (
                          <SelectItem key={opt} value={opt}>
                            {t(`form.evalScaleOptions.${opt}`)}
                          </SelectItem>
                        ))}
                        <SelectItem value="custom">
                          {t('form.evalScaleOptions.custom')}
                        </SelectItem>
                      </SelectContent>
                    </Select>
                    {isCustomScale && (
                      <Input
                        placeholder={t('form.evalScalePlaceholder')}
                        value={currentScale}
                        onChange={(e) => form.setValue('evalScale', e.target.value)}
                      />
                    )}
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">
                    {t('form.evalScaleHelp')}
                  </p>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="identifyingColor"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('form.color')}</FormLabel>
                  <FormControl>
                    <div className="flex flex-wrap gap-1.5 sm:gap-2">
                      {COLORS.map((color) => (
                        <button
                          key={color}
                          type="button"
                          className={`h-7 w-7 sm:h-8 sm:w-8 rounded-lg transition-all ${
                            selectedColor === color
                              ? 'ring-2 ring-offset-2 ring-primary scale-110'
                              : 'hover:scale-105'
                          }`}
                          style={{ backgroundColor: color }}
                          onClick={() => field.onChange(color)}
                          aria-label={`Seleccionar color ${color}`}
                          aria-pressed={selectedColor === color}
                        />
                      ))}
                    </div>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="notes"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('form.notes')}</FormLabel>
                  <FormControl>
                    <Textarea
                      placeholder={t('form.notesPlaceholder')}
                      className="resize-none"
                      rows={3}
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <DialogFooter className="pt-4 flex-col-reverse sm:flex-row gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
                disabled={isLoading}
                className="w-full sm:w-auto"
              >
                {t('cancel')}
              </Button>
              <Button type="submit" disabled={isLoading} className="w-full sm:w-auto">
                {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                {isEditing ? t('save') : t('create')}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
